import { NgIf } from '@angular/common';
import { Component, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild, ViewEncapsulation, forwardRef } from '@angular/core';

import { Subject } from 'rxjs';

import { CheckedState, TreeViewComponent, TreeViewModule } from '@progress/kendo-angular-treeview';

import { ICriteriaControl } from '@myapp/modules/fwcore/components';
import { TreeDataItemCheckHighlighted, QueryPanelSummaryItem, TreeDataItem } from '@myapp/modules/fwcore/models';
import { QueryPanelConnectorService } from '@myapp/modules/fwcore/services';

@Component({
  selector: 'treeview-criteria',
  standalone: true,
  styleUrls: ['./treeview-criteria.component.scss'],
  templateUrl: './treeview-criteria.component.html',
  encapsulation: ViewEncapsulation.None,
  imports: [NgIf, TreeViewModule,
    forwardRef(() => TreeViewCriteriaComponent)]
})
export class TreeViewCriteriaComponent
  implements ICriteriaControl, OnInit, OnDestroy {
  /*-------------------------- Input / Outputs --------------------------------*/
  @Input() public caption: string = "";
  @Input() imagePathPrefix: string = "/assets/images/";
  @Input() public set name(caption: string) {
    this.caption = caption;
  }
  @Output() dataRequired: EventEmitter<undefined> = new EventEmitter();

  @Input() set data(items: Array<TreeDataItemCheckHighlighted> | undefined) {
    let root = new TreeDataItemCheckHighlighted();
    root.id = "*" + this.caption;
    root.text = this.caption;
    root.hasChildren = false;
    root.isLocked = true;

    this._hasData = (items && items.length > 0) ?? false;
    if (this._hasData) {
      root.items = items;
      root.hasChildren = true;
    }

    const container = new Array<TreeDataItemCheckHighlighted>();
    container.push(root);

    this.dataItems = container;

    this.connectorService.isLoaded(this.caption);

    if (this._hasData && this._items) {
      this.setItems(this._items);
    }
  }

  /*-------------------------- Internal properties  ---------------------------*/

  @ViewChild(TreeViewComponent, { static: false })
  protected treeViewComp!: TreeViewComponent;

  protected destroy$ = new Subject();
  protected loading: boolean = true;

  protected dataItems: Array<TreeDataItemCheckHighlighted> = [];
  protected checkedItems: Array<string> = [];
  protected checkedKeys: Array<string> = [];
  protected filterTerm: string = "";

  protected subscription: any;

  private _items?: string[];
  private _hasData: boolean = false;

  /*-------------------------- Constructor  --------------------------*/

  constructor (
    protected readonly connectorService: QueryPanelConnectorService) {
    // subscribe to events from other tree views / summary box
    this.subscription = this.connectorService.onMessage().subscribe(message => {
      // adds will have come from this or other treeviews, we can ignore them
      if (message) {
        switch (message.type) {
          case "delete":
            if (!message.isFromTreeView && message.item && message.item.controlName == this.caption) {
              this.removeItem(message.item);
            }
            break;
          case "clear":
            this.checkedItems = this.checkedKeys = [];
            break;
          case "filter":
            this.filterTerm = message.filter ?? "";
            break;
          case "load":
            if (this._hasData) {
              this.connectorService.isLoaded(this.caption);
            } else {
              this.dataRequired.emit();
            }
            break;
        }
      }
    });
  }

  /*----------------------------------- ng  --------------------------*/

  ngOnInit(): void {
    this.data = [];
  }

  ngOnDestroy(): void {
    if (this.subscription) {
      this.subscription.unsubscribe();
    }
    this.destroy$.next();
    this.destroy$.complete();
  }

  /*-------------------------- ICriteriaControl --------------------------*/

  public getHeaderLine(): string | undefined {
    if (this.checkedItems && this.checkedItems.length == 1 && this.checkedItems[0] == "0") {
      return "all";
    }

    if (this.checkedItems) {
      let result = new Array<string>;
      this.checkedItems.forEach((index) => {
        let item = this.getQueryPanelDataItem(index);
        if (item) {
          result.push(item.text!);
        }
      });
      return result.join("; ");
    }
    return undefined;
  }

  public getParameter(separator?: string): string | undefined {
    return this.getItems()?.join(separator ?? ";");
  }
  /*-------------------------- public functions --------------------------*/

  public isChecked(index: string): boolean {
    return this.checkedItems.indexOf(index) >= 0;
  }

  public removeItem(item: QueryPanelSummaryItem) {
    // asterisk plus name is used as top level ID, means "clear all" when removing
    if (item.id == ("*" + this.caption)) {
      this.checkedItems = this.checkedKeys = [];
      return;
    }

    if (item.index) {
      this.checkedKeys = this.checkedKeys.filter((i) =>
        !i.startsWith(item.index!));
      this.checkedItems = this.getTrimmedCheckedKeys();
    }
  }

  public getItems(): string[] | undefined {
    if (this.checkedItems && this.checkedItems.length == 1 && this.checkedItems[0] == "0") {
      return undefined;
    }

    if (this.checkedItems) {
      let result = new Array<string>;
      this.checkedItems.forEach((index) => {
        let item = this.getQueryPanelDataItem(index);
        if (item) {
          result.push(item.id!);
        }
      });
      return result;
    }
    return undefined;
  }

  public setItems(items?: string[]) {
    if (!this._hasData) {
      this._items = items;
      return;
    }
    let idIndexMap = new Map<string, string>();
    let allItems = new Array<string>();

    if (this.dataItems[0].items) {
      this.dataItems[0].items!.forEach((d0, ix) => {
        if (d0.id) { idIndexMap.set(d0.id, `0_${ix}`); allItems.push(`0_${ix}`); }
        if (d0.items) {
          d0.items!.forEach((d1, ix1) => {
            if (d1.id) { idIndexMap.set(d1.id, `0_${ix}_${ix1}`); allItems.push(`0_${ix}_${ix1}`); }
            if (d1.items) {
              d1.items!.forEach((d2, ix2) => {
                if (d2.id) { idIndexMap.set(d2.id, `0_${ix}_${ix1}_${ix2}`); allItems.push(`0_${ix}_${ix1}_${ix2}`); }
              });
            }
          });
        }
      });
    }

    let checked = new Array<string>();
    if (items && items.length > 0) {
      items.forEach((ci) => {
        let ix = idIndexMap.get(ci);
        if (ix) {
          checked.push(ix);
          let qpitem = this.getQueryPanelDataItem(ix);
          this.connectorService.addItem(qpitem!, true);
          allItems.forEach(x => {
            if (x.startsWith(ix) && x != ix) {
              checked.push(x);
            }
          });
        }
      });
    }
    this.checkedKeys = checked;
  }

  /*--------------------------- event handlers  --------------------------*/

  protected handleChecking(e?: any): void {
    let trimmedKeys = this.getTrimmedCheckedKeys();

    // remove all not in trimmed keys
    const removeItems = this.checkedItems.filter((i) => trimmedKeys.indexOf(i) == -1);
    removeItems.forEach((index) => {
      let item = this.getQueryPanelDataItem(index);
      if (item) {
        this.connectorService.removeItem(item, true);
      }
    });

    // add all the new elements
    const addItems = trimmedKeys.filter((i) => this.checkedItems.indexOf(i) == -1);
    addItems.forEach((index) => {
      let item = this.getQueryPanelDataItem(index);
      if (item) {
        this.connectorService.addItem(item, true);
      }
    });

    this.checkedItems = trimmedKeys;
  }

  /*-------------------------- internal functions --------------------------*/

  protected isItemChecked(_: any, index: string): CheckedState {
    if (this.checkedKeys.includes(index)) {
      return "checked";
    }

    const hasCheckedChildren = this.checkedKeys.some((k) =>
      k.startsWith(index + "_")
    );

    if (hasCheckedChildren) {
      return "indeterminate";
    }

    return "none";
  }

  protected getTreeItemByIndex(index: string): TreeDataItemCheckHighlighted | undefined {
    let indices = index.split("_");
    let result = this.dataItems[Number(indices[0])];

    if (indices.length > 1) {
      for (let i = 1; i < indices.length; i++) {
        let j = Number(indices[i]);
        if (result?.items) {
          result = result.items[j];
        }
      }
    }

    return result;
  }

  /*-------------------------- private functions --------------------------*/

  private getQueryPanelDataItem(index: string): QueryPanelSummaryItem | undefined {
    let treeItem = this.getTreeItemByIndex(index);
    if (treeItem) {
      let result = new QueryPanelSummaryItem();
      result.controlName = this.caption;
      result.id = treeItem.id!;
      result.text = treeItem.text!;
      result.index = index
      return result;
    }
    return undefined;
  }

  // the array in this function needs a binary sort, not an alphabetic one
  private getTrimmedCheckedKeys() {
    this.checkedKeys.sort();
    let result: Array<string> = [];
    let ignored: Array<string> = [];

    this.checkedKeys.forEach((key) => {
      let pai = this.getParent(key);
      let avo = this.getParent(pai);
      let bis = this.getParent(avo);

      if ((result.indexOf(pai) == -1) && (ignored.indexOf(pai) == -1)
        && (result.indexOf(avo) == -1) && (ignored.indexOf(avo) == -1)
        && (result.indexOf(bis) == -1) && (ignored.indexOf(bis) == -1)
      ) {
        if (this.isItemChecked(this, key) == "checked") {
          result.push(key);
        } else {
          ignored.push(key);
        }
      }
    });

    return result;
  }

  private getParent(key: string): string {
    let ix = key.lastIndexOf('_');
    if (ix == -1) {
      return "x";
    }
    return key.substring(0, ix);
  }
}
